import { makeAutoObservable } from 'mobx';

import ToastifyService from 'src/services/ToastifyService';
import ConfirmManager, { ConfirmActionsTypes } from 'src/libs/ui-kit/confirm-dialog/ConfirmManager';

import { CardModal } from '../components/CardModal';

import type { ICard, IProvider } from '../interfaces';

export class PaymentMethodsStore {
  private readonly service: IProvider;
  private readonly userId: string;

  cardAttaching = false;

  paymentMethodsLoading = false;
  cardsList: ICard[] = [];

  chosenCardId?: string;
  methodDetailsLoading = false;

  isAddingTask = false;

  private deleteCardPending = new Set<string>();

  isCardDeletePending(cardId: string) {
    return this.deleteCardPending.has(cardId);
  }

  private get cardsIndex(): { readonly [id: string]: ICard } {
    return this.cardsList.reduce<Record<string, ICard>>((acc, card) => {
      acc[card.id!] = card;
      return acc;
    }, {});
  }

  get chosenCard(): ICard | undefined {
    if (!this.chosenCardId) return;

    return this.cardsIndex[this.chosenCardId];
  }

  get viewModalOpen(): boolean {
    return !!this.chosenCard;
  }

  constructor({ userId, serviceProvider }: { userId: string; serviceProvider: IProvider }) {
    this.userId = userId;
    this.service = serviceProvider;

    makeAutoObservable(this, undefined, { autoBind: true });

    this.fetchCardsList(userId).catch(this.emitError);
  }

  private async fetchCardsList(userId: string) {
    this.paymentMethodsLoading = true;

    try {
      const { cards } = await this.service.getCardsList({ userId });

      this.cardsList = cards;
      this.paymentMethodsLoading = false;
    } catch (e) {
      this.paymentMethodsLoading = false;
      throw e;
    }
  }

  handleRemoveCard(card: ICard) {
    ConfirmManager.show({
      title: 'Удалить карту',
      message: CardModal,
      onConfirm: async () => {
        ConfirmManager.hide();
        await this.removeCard(card);
      },
      variant: ConfirmActionsTypes.DANGER,
      data: card,
      size: 'sm',
    }).catch(this.emitError);
  }

  handleChangeRootCard(card: ICard) {
    ConfirmManager.show({
      title: 'Сделать основным',
      message: CardModal,
      onConfirm: async () => {
        ConfirmManager.hide();
        await this.changeRootCard(card);
      },
      variant: ConfirmActionsTypes.DEFAULT,
      data: card,
      size: 'sm',
    }).catch(this.emitError);
  }

  emitError({ message = 'Возникла техническая ошибка, попробуйте позже' }: { message?: string } = {}) {
    ToastifyService.toast(message, { type: 'error' });
  }

  showCardInfoModal(cardId: string) {
    this.chosenCardId = cardId;
  }

  closeCardInfoModal() {
    this.chosenCardId = undefined;
  }

  attachCard() {
    if (this.cardAttaching) return;

    this.cardAttaching = true;

    this.service
      .attachCard({ userId: this.userId })
      .then(() => ToastifyService.toast('На основной номер отправлено СМС', { type: 'success' }))
      .catch(() => this.emitError({ message: 'Не удалось отправить СМС. Повторите попытку' }))
      .finally(() => {
        this.cardAttaching = false;
      });
  }

  /**
   * Временная функция для добавления задачи.
   * В будущем переедет в виджет "Покупки"
   */
  async addTask() {
    this.isAddingTask = true;

    try {
      const { id: taskId } = await this.service.addTask({ id: this.userId });

      window.open(`https://crm.cloud.qlean.ru/task/${taskId}`);
    } catch {
      ToastifyService.toast('Заявка не создана', { type: 'error' });
    }

    this.isAddingTask = false;
  }

  private async removeCard(card: ICard) {
    if (!card.id) return;

    this.deleteCardPending.add(card.id);

    try {
      await this.service.removeCard({ cardId: card.id, userId: this.userId });

      ToastifyService.toast(`Карта успешно удалена: номер: ${card.cardNumber}, месяц / год: ${card.expirationDate}`, {
        type: 'success',
      });

      await this.fetchCardsList(this.userId);
    } finally {
      this.deleteCardPending.delete(card.id);
    }
  }

  private async changeRootCard(card: ICard) {
    if (!card.id) return;

    await this.service.changeRootCard({
      cardId: card.id,
      userId: this.userId,
    });

    ToastifyService.toast(
      `Основная карта успешно изменена: номер: ${card.cardNumber}, месяц / год: ${card.expirationDate}`,
      { type: 'success' },
    );

    await this.fetchCardsList(this.userId);
  }
}
